import React, { useState } from 'react';
import { Search, MapPin, Users, Briefcase } from 'lucide-react';
import type { JobDescription, SearchFilters, SearchRequest } from '../shared/types';

interface SearchFormProps {
  onSearch: (request: SearchRequest) => void;
  isLoading: boolean;
}

export function SearchForm({ onSearch, isLoading }: SearchFormProps) {
  const [jobDescription, setJobDescription] = useState<JobDescription>({
    title: '',
    company: '',
    description: '',
    requirements: [''],
    preferredSkills: [''],
    experienceLevel: 'mid',
    location: '',
    remoteOk: false,
  });

  const [filters, setFilters] = useState<SearchFilters>({
    minYearsExperience: undefined,
    maxYearsExperience: undefined,
  });

  const [limit, setLimit] = useState(10);

  const handleRequirementChange = (index: number, value: string) => {
    const newRequirements = [...jobDescription.requirements];
    newRequirements[index] = value;
    setJobDescription({ ...jobDescription, requirements: newRequirements });
  };

  const addRequirement = () => {
    setJobDescription({
      ...jobDescription,
      requirements: [...jobDescription.requirements, ''],
    });
  };

  const removeRequirement = (index: number) => {
    const newRequirements = jobDescription.requirements.filter((_, i) => i !== index);
    setJobDescription({ ...jobDescription, requirements: newRequirements });
  };

  const handlePreferredSkillChange = (index: number, value: string) => {
    const newSkills = [...(jobDescription.preferredSkills || [])];
    newSkills[index] = value;
    setJobDescription({ ...jobDescription, preferredSkills: newSkills });
  };

  const addPreferredSkill = () => {
    setJobDescription({
      ...jobDescription,
      preferredSkills: [...(jobDescription.preferredSkills || []), ''],
    });
  };

  const removePreferredSkill = (index: number) => {
    const newSkills = (jobDescription.preferredSkills || []).filter((_, i) => i !== index);
    setJobDescription({ ...jobDescription, preferredSkills: newSkills });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Filter out empty requirements and skills
    const filteredRequirements = jobDescription.requirements.filter(req => req.trim() !== '');
    const filteredPreferredSkills = (jobDescription.preferredSkills || []).filter(skill => skill.trim() !== '');
    
    if (filteredRequirements.length === 0) {
      alert('Please add at least one requirement');
      return;
    }

    const searchRequest: SearchRequest = {
      jobDescription: {
        ...jobDescription,
        requirements: filteredRequirements,
        preferredSkills: filteredPreferredSkills.length > 0 ? filteredPreferredSkills : undefined,
      },
      filters: {
        ...filters,
        // Only include filters that have values
        ...(filters.minYearsExperience !== undefined && { minYearsExperience: filters.minYearsExperience }),
        ...(filters.maxYearsExperience !== undefined && { maxYearsExperience: filters.maxYearsExperience }),
      },
      limit,
    };

    onSearch(searchRequest);
  };

  return (
    <form onSubmit={handleSubmit} className="bg-gray-800 rounded-lg shadow-md p-6 space-y-6 border border-gray-700">
      <div className="flex items-center space-x-2 mb-4">
        <Search className="w-6 h-6 text-primary-600" />
        <h2 className="text-2xl font-bold text-white">Job Description</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Job Title *
          </label>
          <input
            type="text"
            required
            value={jobDescription.title}
            onChange={(e) => setJobDescription({ ...jobDescription, title: e.target.value })}
            className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder-gray-400"
            placeholder="e.g., Senior Full Stack Developer"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Company *
          </label>
          <input
            type="text"
            required
            value={jobDescription.company}
            onChange={(e) => setJobDescription({ ...jobDescription, company: e.target.value })}
            className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder-gray-400"
            placeholder="e.g., Tech Innovation Inc"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Job Description *
        </label>
        <textarea
          required
          rows={4}
          value={jobDescription.description}
          onChange={(e) => setJobDescription({ ...jobDescription, description: e.target.value })}
          className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder-gray-400"
          placeholder="Describe the role, responsibilities, and what you're looking for..."
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Requirements *
        </label>
        {jobDescription.requirements.map((requirement, index) => (
          <div key={index} className="flex items-center space-x-2 mb-2">
            <input
              type="text"
              value={requirement}
              onChange={(e) => handleRequirementChange(index, e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder-gray-400"
              placeholder="e.g., 5+ years of React experience"
            />
            {jobDescription.requirements.length > 1 && (
              <button
                type="button"
                onClick={() => removeRequirement(index)}
                className="px-3 py-2 text-red-400 hover:text-red-300"
              >
                Remove
              </button>
            )}
          </div>
        ))}
        <button
          type="button"
          onClick={addRequirement}
          className="text-blue-400 hover:text-blue-300 text-sm"
        >
          + Add Requirement
        </button>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Preferred Skills
        </label>
        {(jobDescription.preferredSkills || []).map((skill, index) => (
          <div key={index} className="flex items-center space-x-2 mb-2">
            <input
              type="text"
              value={skill}
              onChange={(e) => handlePreferredSkillChange(index, e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder-gray-400"
              placeholder="e.g., TypeScript"
            />
            <button
              type="button"
              onClick={() => removePreferredSkill(index)}
              className="px-3 py-2 text-red-400 hover:text-red-300"
            >
              Remove
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={addPreferredSkill}
          className="text-blue-400 hover:text-blue-300 text-sm"
        >
          + Add Preferred Skill
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            <Briefcase className="w-4 h-4 inline mr-1" />
            Experience Level
          </label>
          <select
            value={jobDescription.experienceLevel}
            onChange={(e) => setJobDescription({ 
              ...jobDescription, 
              experienceLevel: e.target.value as 'entry' | 'mid' | 'senior' | 'lead'
            })}
            className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="entry">Entry Level</option>
            <option value="mid">Mid Level</option>
            <option value="senior">Senior Level</option>
            <option value="lead">Lead/Principal</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            <MapPin className="w-4 h-4 inline mr-1" />
            Location
          </label>
          <input
            type="text"
            value={jobDescription.location}
            onChange={(e) => setJobDescription({ ...jobDescription, location: e.target.value })}
            className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder-gray-400"
            placeholder="e.g., San Francisco, CA"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            <Users className="w-4 h-4 inline mr-1" />
            Results Limit
          </label>
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value={5}>5 students</option>
            <option value={10}>10 students</option>
            <option value={20}>20 students</option>
            <option value={50}>50 students</option>
          </select>
        </div>
      </div>

      <div className="flex items-center space-x-4">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={jobDescription.remoteOk}
            onChange={(e) => setJobDescription({ ...jobDescription, remoteOk: e.target.checked })}
            className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500 focus:ring-2"
          />
          <span className="ml-2 text-sm text-gray-300">Remote OK</span>
        </label>
      </div>

      <div className="border-t pt-4">
        <h3 className="text-lg font-medium text-white mb-4">Additional Filters</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Minimum Years Experience
            </label>
            <input
              type="number"
              min="0"
              max="50"
              value={filters.minYearsExperience || ''}
              onChange={(e) => setFilters({ 
                ...filters, 
                minYearsExperience: e.target.value ? Number(e.target.value) : undefined 
              })}
              className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder-gray-400"
              placeholder="e.g., 3"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Maximum Years Experience
            </label>
            <input
              type="number"
              min="0"
              max="50"
              value={filters.maxYearsExperience || ''}
              onChange={(e) => setFilters({ 
                ...filters, 
                maxYearsExperience: e.target.value ? Number(e.target.value) : undefined 
              })}
              className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder-gray-400"
              placeholder="e.g., 10"
            />
          </div>
        </div>
      </div>

      <button
        type="submit"
        disabled={isLoading}
        className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
      >
        {isLoading ? (
          <>
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            <span>Searching...</span>
          </>
        ) : (
          <>
            <Search className="w-4 h-4" />
            <span>Search Students</span>
          </>
        )}
      </button>
    </form>
  );
}
